import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SchedulerEvent } from '@progress/kendo-angular-scheduler';
import { from, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { OrderBase, PaymentStatus, ReservationSetStatus } from '../cart/cart.model';
import { defaultCustomerGroupId } from '../core/catalog.constants';
import { readFiles } from '../core/file-utilities';
import { ItemService } from '../core/item.service';
import { GiftCardAccountingModel, GiftCardModel } from '../gift-card/gift-card.models';
import { AvailabilityPlanWithPricePlans } from '../models/availability-plans';
import { FieldLookupValue } from '../models/field-lookup-value';
import { ItemType } from '../models/item';
import { ItemTreeHierarchy, TreeAndItems } from '../root-store/object-tree-store/object-tree.models';
import { IdentityResult } from '../user/user.interfaces';
import { getIconName } from '../utilities/items';
import { AvailabilityPlan, AvailabilityPlanAppointment } from './availability-plans/models';
import { AdminHomeModel } from './home/home.model';
import { ItemEditModel, ItemSummary } from './items/items.models';
import { BlockAppointmentSummary, CalendarShareModel, CalendarShareType, CalendarSyncDuration, CustomerGroup, ItemPermission, ItemPermissionType, Janitor, MailTemplate, OrderSummary, PagedResult, PricePlan, ReservationPrint } from './models/admin.model';
import { InvoiceExport, UtilizationExport } from './reservations-export/reservations-export.models';
import { Role, RoleMemberInfo, RoleSummary } from './role-management/roles.models';
import { SxcConfig } from './sxc/sxc.model';
import { CatalogSummary } from './tenants/tenants.models';
import { TextsAndLabels } from './texts-and-labels/models';

@Injectable({
  providedIn: 'root'
})
export class AdminBackendService {
  private readonly baseUrl = '/api'
  private itemsTreeForReservationManagement$: Observable<TreeAndItems> | undefined

  constructor(private http: HttpClient) { }

  // #region Items
  getItems(itemType: ItemType | null = null): Observable<ItemSummary[]> {
    const params: { [param: string]: string } = itemType ? { itemType } : {}

    return this.http.get<ItemSummary[]>(`${this.baseUrl}/Admin/Items`, { params })
      .pipe(map(items => items.map(i => ({ ...i, materialIcon: getIconName(i.itemType) }))))
  }

  getItem(id: number): Observable<ItemEditModel> {
    return this.http.get<ItemEditModel>(`${this.baseUrl}/Admin/Items(${id})`)
  }

  addItem(item: ItemEditModel, copyOfItemId: number | null = null): Observable<ItemEditModel> {
    const params: { [param: string]: number } = copyOfItemId ? { copyOfItemId } : {}
    return this.http.post<ItemEditModel>(`${this.baseUrl}/Admin/Items`, item, { params })
  }

  updateItem(item: ItemEditModel): Observable<ItemEditModel> {
    return this.http.put<ItemEditModel>(`${this.baseUrl}/Admin/Items(${item.id})`, item)
  }

  deleteItem(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/Admin/Items(${id})`)
  }

  getSxcConfig(): Observable<SxcConfig> {
    return this.http.get<SxcConfig>(`${this.baseUrl}/Admin/SxcConfig`)
  }
  // #endregion

  clearSxcCache(appId: number): Observable<string> {
    return this.http.get(`/api/sxc/sys/insights/purge?appid=${appId}`, { responseType: 'text' })
  }

  // #region CustomerGroup

  getCustomerGroups(includeFromSharedCatalogs = false): Observable<CustomerGroup[]> {
    const params: { [param: string]: string | string[] } = {}
    if (includeFromSharedCatalogs)
      params['includeFromSharedCatalogs'] = 'true'

    return this.http.get<CustomerGroup[]>(`${this.baseUrl}/CustomerGroups`, { params })
      .pipe(
        map(groups => groups.map(g => ({
          ...g,
          isDefaultGroup: g.id === defaultCustomerGroupId,
        }))))
  }

  addCustomerGroup(name: string): Observable<CustomerGroup> {
    return this.http.post<CustomerGroup>(`${this.baseUrl}/CustomerGroups?name=${name}`, {})
  }

  deleteCustomerGroup(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/CustomerGroups(${id})`, {})
  }

  updateCustomerGroup(id: number, name: string): Observable<CustomerGroup> {
    return this.http.put<CustomerGroup>(`${this.baseUrl}/CustomerGroups(${id})?name=${name}`, {})
  }

  //#endregion

  // #region PricePlan

  getPricePlans(): Observable<PricePlan[]> {
    return this.http.get<PricePlan[]>(`${this.baseUrl}/PricePlans`)
  }

  addPricePlan(pricePlan: PricePlan): Observable<PricePlan> {
    return this.http.post<PricePlan>(`${this.baseUrl}/PricePlans`, undefined, { params: this.getPricePlanParams(pricePlan) })
  }

  deletePricePlan(pricePlanId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/PricePlans(${pricePlanId})`, {})
  }

  updatePricePlan(pricePlan: PricePlan): Observable<PricePlan> {
    return this.http.put<PricePlan>(`${this.baseUrl}/PricePlans(${pricePlan.id})`, undefined, { params: this.getPricePlanParams(pricePlan) })
  }

  private getPricePlanParams(pricePlan: PricePlan) {
    const params: { [param: string]: string | number } = {
      name: pricePlan.name
    }

    if (pricePlan.from)
      params['from'] = pricePlan.from
    if (pricePlan.to)
      params['to'] = pricePlan.to

    return params
  }

  // #endregion

  // #region GiftCards

  getGiftCards(): Observable<GiftCardModel[]> {
    return this.http.get<GiftCardModel[]>(`${this.baseUrl}/GiftCards`)
  }

  addGiftCard(initialAmount: number, expiresOn: Date, senderName: string | null, recipientName: string | null, message: string | null, adminComment: string | null) {
    return this.http.post<GiftCardModel>(`${this.baseUrl}/GiftCards`, { initialAmount, expiresOn, senderName, recipientName, message, adminComment })
  }

  updateGiftCard(id: number, status: string, expiresOn: Date, senderName: string | null, recipientName: string | null, message: string | null, adminComment: string | null) {
    return this.http.put<GiftCardModel>(`${this.baseUrl}/GiftCards(${id})`, { status, expiresOn, senderName, recipientName, message, adminComment })
  }

  getGiftCardAccounting(giftCardId: number) {
    return this.http.get<GiftCardAccountingModel[]>(`${this.baseUrl}/GiftCards(${giftCardId})/Accounting`)
  }

  addGiftCardAccounting(giftCardId: number, amount: number, text: string) {
    return this.http.post<GiftCardAccountingModel>(`${this.baseUrl}/GiftCards(${giftCardId})/Accounting`, { amount, text })
  }

  // #endregion

  // #region Orders

  getOrderCount(): Observable<number> {
    return this.http.get<number>(`${this.baseUrl}/Orders/Count`)
  }

  getOrders(rangeStart: Date | null, rangeEnd: Date | null, status: ReservationSetStatus | null, itemIds: number[] | null,
    searchQuery: string | null, skip: number | undefined, top: number | undefined, orderBy: string | undefined, orderByDir: 'asc' | 'desc' | undefined):
    Observable<PagedResult<OrderSummary>> {
    const params: { [param: string]: string | string[] } = {}

    if (rangeStart)
      params['rangeStart'] = rangeStart.toJSON()
    if (rangeEnd)
      params['rangeEnd'] = rangeEnd.toJSON()
    if (status)
      params['status'] = status
    if (itemIds?.length)
      params['itemIds'] = itemIds.toString()
    if (searchQuery)
      params['searchQuery'] = searchQuery
    if (skip)
      params['skip'] = skip.toString()
    if (top)
      params['top'] = top.toString()
    if (orderBy)
      params['orderBy'] = orderBy
    if (orderByDir)
      params['orderByDir'] = orderByDir

    return this.http.get<PagedResult<OrderSummary>>(`${this.baseUrl}/Orders`, { params })
  }

  getReservationsForPrint(
    rangeStart: Date | null, rangeEnd: Date | null, status: ReservationSetStatus | null,
    itemIds: number[]): Observable<ReservationPrint[]> {
    const params: { [param: string]: string | string[] } = {}

    if (rangeStart)
      params['rangeStart'] = rangeStart.toJSON()
    if (rangeEnd)
      params['rangeEnd'] = rangeEnd.toJSON()
    if (status)
      params['status'] = status
    if (itemIds.length)
      params['itemIds'] = itemIds.toString()

    return this.http.get<ReservationPrint[]>(`${this.baseUrl}/Orders/Print`, { params })
  }

  getInvoices(rangeStart: Date, rangeEnd: Date, filterType: 'SysCreated' | 'ReservationDate'): Observable<InvoiceExport[]> {
    const params: { [param: string]: string | string[] } = {
      rangeStart: rangeStart.toJSON(),
      rangeEnd: rangeEnd.toJSON(),
      filterType
    }

    return this.http.get<InvoiceExport[]>(`${this.baseUrl}/Orders/Invoices`, { params })
      .pipe(map(items => items.map(i => ({
        ...i,
        sysCreated: new Date(i.sysCreated),
        firstReservation: i.firstReservation ? new Date((i as any).firstReservation) : undefined,
        lastReservation: i.lastReservation ? new Date((i as any).lastReservation) : undefined
      }))))
  }

  getUtilization(rangeStart: Date, rangeEnd: Date, filterType: 'SysCreated' | 'ReservationDate', itemIds: number[]):
    Observable<UtilizationExport[]> {
    const params: { [param: string]: string | string[] } = {
      rangeStart: rangeStart.toJSON(),
      rangeEnd: rangeEnd.toJSON(),
      filterType
    }

    if (itemIds?.length)
      params['itemIds'] = itemIds.toString()

    return this.http.get<UtilizationExport[]>(`${this.baseUrl}/Orders/Utilization`, { params })
      .pipe(map(items => items.map(i => ({
        ...i,
        sysCreated: new Date(i.sysCreated),
        startTime: i.startTime ? new Date(i.startTime) : null,
        endTime: i.endTime ? new Date(i.endTime) : null
      }))))
  }

  //#endregion

  // #region BlockAppointments

  getBlockCount(): Observable<number> {
    return this.http.get<number>(`${this.baseUrl}/Blocks/Count`)
  }

  getBlockAppointments(rangeStart: Date | null, rangeEnd: Date | null, itemIds: number[]): Observable<BlockAppointmentSummary[]> {
    const params: { [param: string]: string | string[] } = {}

    if (rangeStart)
      params['rangeStart'] = rangeStart.toJSON()
    if (rangeEnd)
      params['rangeEnd'] = rangeEnd.toJSON()
    if (itemIds.length)
      params['itemIds'] = itemIds.toString()

    return this.http.get<BlockAppointmentSummary[]>(`${this.baseUrl}/Blocks`, { params })
  }

  //#endregion

  getAdminHome(): Observable<AdminHomeModel> {
    return this.http.get<AdminHomeModel>(`${this.baseUrl}/Admin/Home`)
  }

  // #region Setting

  getSettings(catalogId: number): Observable<{ [key: string]: string }> {
    return this.http.get<{ [key: string]: string }>(`${this.baseUrl}/Admin/Settings`,
      { params: { CatalogId: catalogId.toString() } }
    )
  }

  getSetting(keys: string[]): Observable<{ [key: string]: string }> {
    return this.http.get<{ [key: string]: string }>(`${this.baseUrl}/Admin/Settings?keys=${keys}`)
  }

  updateSetting(key: string, value: string): Observable<any> {
    return this.http.put(`${this.baseUrl}/Admin/Settings(${key})`, { value })
  }

  //#endregion

  // #region ItemTree
  getItemsTreeForReservationManagement(): Observable<TreeAndItems> {
    if (!this.itemsTreeForReservationManagement$)
      this.itemsTreeForReservationManagement$ = this.http.get<TreeAndItems>(`${this.baseUrl}/ItemTree/ReservationManagement`)
        .pipe(
          tap(treeAndItems => ItemService.prepareItemTreeData(treeAndItems)),
          shareReplay(1)
        )

    return this.itemsTreeForReservationManagement$
  }


  addTreeItem(itemId: number, order: number, parentId?: number): Observable<ItemTreeHierarchy> {
    const params: { [param: string]: string | number | boolean } = {
      itemId,
      order
    }

    if (parentId)
      params['parentId'] = parentId

    return this.http.post<ItemTreeHierarchy>(`${this.baseUrl}/ItemTree`, undefined, { params })
  }

  updateTreeItem(id: number, order: number, parentId: number | undefined, expand: boolean | undefined): Observable<void> {
    const params: { [param: string]: string | number | boolean } = {
      order,
      parentId: parentId ? parentId : '',
      expand: expand ?? false
    }

    return this.http.put<void>(`${this.baseUrl}/ItemTree(${id})`, undefined, { params })
  }

  updateTreeOrder(newOrder: { [key: number]: number }) {
    return this.http.patch<void>(`${this.baseUrl}/ItemTree/Order`, undefined, { params: { newOrder: JSON.stringify(newOrder) } })
  }

  deleteTreeItem(treeItemId: number): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/ItemTree(${treeItemId})`)
  }

  // #endregion

  // #region users

  addAdminUser(user: Janitor): Observable<IdentityResult> {
    return this.http.post<IdentityResult>(`${this.baseUrl}/Admin/Users`, user)
  }

  getAdminUser(id: string): Observable<Janitor> {
    return this.http.get<Janitor>(`${this.baseUrl}/Admin/Users/Details`, { params: { userName: id } })
  }

  getAdminUsers(): Observable<Janitor[]> {
    return this.http.get<Janitor[]>(`${this.baseUrl}/Admin/Users`)
  }

  searchCustomer(query: string): Observable<Janitor[]> {
    return this.http.get<Janitor[]>(`${this.baseUrl}/Admin/Users/Customer`, { params: { query } })
  }

  updateAdminUser(id: string, user: Janitor): Observable<Janitor> {
    const params: { [param: string]: string | string[] } = {}
    params['userName'] = id

    return this.http.put<Janitor>(`${this.baseUrl}/Admin/Users`, user, { params })
  }

  deleteAdminUser(id: string): Observable<null> {
    const params: { [param: string]: string | string[] } = {}
    params['userName'] = id
    return this.http.delete<null>(`${this.baseUrl}/Admin/Users`, { params })
  }

  resetAdminPassword(id: string): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/User/ForgotPassword`, { email: id })
  }

  // #endregion

  // #region Roles / Role-Members

  getRoles(): Observable<RoleSummary[]> {
    return this.http.get<RoleSummary[]>(`${this.baseUrl}/Roles`)
  }

  getRole(id: string): Observable<Role> {
    return this.http.get<Role>(`${this.baseUrl}/Roles(${id})`)
  }

  addRole(role: Role): Observable<Role> {
    return this.http.post<Role>(`${this.baseUrl}/Roles`, role)
  }

  updateRole(id: string, role: Role): Observable<Role> {
    return this.http.put<Role>(`${this.baseUrl}/Roles(${id})`, role)
  }

  deleteRole(id: string): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/Roles(${id})`)
  }

  getRoleMembers(id: string) {
    return this.http.get<RoleMemberInfo[]>(`${this.baseUrl}/Roles(${id})/Members`)
  }

  deleteRoleMember(roleId: string, userName: string): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/Roles(${roleId})/Members`, { params: { userName } })
  }

  addRoleMember(roleId: string, userName: string): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/Roles(${roleId})/Members`, {}, { params: { userName } })
  }

  // #endregion

  // #region ItemPermissions

  getItemPermissionTypes(): Observable<ItemPermissionType[]> {
    return this.http.get<ItemPermissionType[]>(`${this.baseUrl}/ItemPermissions/Types`)
  }

  getItemPermissionsRole(id: string): Observable<ItemPermission[]> {
    return this.http.get<ItemPermission[]>(`${this.baseUrl}/ItemPermissions/Role(${id})`)
  }

  addItemPermissionRole(itemPermission: ItemPermission): Observable<ItemPermission> {
    const params: { [param: string]: string | number } = {
      'permission': itemPermission.permission
    }

    if (itemPermission.itemId)
      params['itemId'] = itemPermission.itemId

    return this.http.post<ItemPermission>(`${this.baseUrl}/ItemPermissions/Role(${itemPermission.id})`, {}, { params })
  }

  getItemPermissionsUser(id: string): Observable<ItemPermission[]> {
    return this.http.get<ItemPermission[]>(`${this.baseUrl}/ItemPermissions/User`, { params: { userName: id } })
  }

  addItemPermissionUser(itemPermission: ItemPermission): Observable<ItemPermission> {
    const params: { [param: string]: string | number } = {
      'userName': itemPermission.id,
      'permission': itemPermission.permission
    }

    if (itemPermission.itemId)
      params['itemId'] = itemPermission.itemId

    return this.http.post<ItemPermission>(`${this.baseUrl}/ItemPermissions/User`, {}, { params })
  }

  deleteItemPermission(id: string): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/ItemPermissions(${id})`)
  }

  // #endregion

  // #region Email Templates

  getEmailTemplates(): Observable<MailTemplate[]> {
    return this.http.get<MailTemplate[]>(`${this.baseUrl}/MailTemplates`)
  }

  testMailTemplate(to: string, key: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/MailTemplates/Test`, {}, { params: { to, key } })
  }

  addMailTemplate(key: string) {
    return this.http.post<MailTemplate>(`${this.baseUrl}/MailTemplates`, undefined, { params: { key } })
  }

  deleteMailTemplate(key: string) {
    return this.http.delete<void>(`${this.baseUrl}/MailTemplates(${key})`)
  }

  // #endregion

  //#region SplashImage

  addSplashImage(image: File): Observable<File> {
    const file = new FormData()
    file.append('file', image, image.name)
    return this.http.post<File>(`${this.baseUrl}/Admin/UploadSplashImage`, file)
  }

  deleteSplashImage(): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/Admin/SplashImage`)
  }

  //#endregion

  //#region Availability Plans

  getAvailabilityPlans(): Observable<AvailabilityPlanWithPricePlans[]> {
    return this.http.get<AvailabilityPlanWithPricePlans[]>(`${this.baseUrl}/AvailabilityPlans`)
  }

  getAvailabilityPlan(id: number): Observable<AvailabilityPlan> {
    return this.http.get<AvailabilityPlan>(`${this.baseUrl}/AvailabilityPlans(${id})`)
  }

  addAvailabilityPlan(plan: AvailabilityPlan): Observable<AvailabilityPlan> {
    return this.http.post<AvailabilityPlan>(`${this.baseUrl}/AvailabilityPlans`, plan)
  }

  deleteAvailabilityPlan(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/AvailabilityPlans(${id})`)
  }

  updateAvailabilityPlan(plan: AvailabilityPlan): Observable<AvailabilityPlan> {
    return this.http.put<AvailabilityPlan>(`${this.baseUrl}/AvailabilityPlans(${plan.id})`, plan)
  }

  copyAvailabilityPlan(id: string): Observable<AvailabilityPlan> {
    return this.http.post<AvailabilityPlan>(`${this.baseUrl}/AvailabilityPlans(${id})/Copy`, {})
  }

  getAvailabilityPlanAppointments(id: number, start: Date, end: Date): Observable<AvailabilityPlanAppointment[]> {
    const params: { [param: string]: string } = {
      rangeStart: start.toJSON(),
      rangeEnd: end.toJSON()
    }

    return this.http.get<AvailabilityPlanAppointment[]>(`${this.baseUrl}/AvailabilityPlans(${id})/Appointments`, { params })
  }

  deleteAvailabilityPlanAppointment(id: number | string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/AvailabilityPlans/Appointments(${id})`)
  }

  getAvailabilityPlanAppointment(id: string): Observable<AvailabilityPlanAppointment> {
    return this.http.get<AvailabilityPlanAppointment>(`${this.baseUrl}/AvailabilityPlans/Appointments(${id})`)
  }

  updateAvailabilityPlanAppointment(plan: (AvailabilityPlanAppointment | SchedulerEvent)): Observable<AvailabilityPlanAppointment> {
    return this.http.put<AvailabilityPlanAppointment>(`${this.baseUrl}/AvailabilityPlans/Appointments(${plan.id})`, plan)
  }

  addAvailabilityPlanAppointment(plan: AvailabilityPlanAppointment): Observable<AvailabilityPlanAppointment> {
    return this.http.post<AvailabilityPlanAppointment>(`${this.baseUrl}/AvailabilityPlans/Appointments`, plan)
  }

  //#endregion

  getCatalogs(): Observable<CatalogSummary[]> {
    return this.http.get<CatalogSummary[]>(`${this.baseUrl}/Catalog`)
  }

  //#region Admin Cart

  updateOrder(order: OrderBase, mailAttachments: File[], sendStatusMailToCustomer = false): Observable<void> {
    const params = {} as { [param: string]: string }

    if (sendStatusMailToCustomer)
      params['sendStatusMailToCustomer'] = 'true';

    const attachments = sendStatusMailToCustomer ? from(readFiles(mailAttachments)) : of([])

    return attachments
      .pipe(
        switchMap(attachments => {
          const body: any = { ...order }
          if (attachments.length)
            body.mailAttachments = attachments

          return this.http.put<void>(`${this.baseUrl}/Cart/Order`, body, { params })
        }))
  }

  changeOrderStatus(newStatus: ReservationSetStatus, mailAttachments: File[], sendMailToCustomer: boolean = true): Observable<void> {
    const params = { newStatus } as { [param: string]: string }

    if (!sendMailToCustomer)
      params['sendMailToCustomer'] = 'false'

    // send attachments to the backend only when needed
    const attachments = sendMailToCustomer && newStatus !== ReservationSetStatus.Closed ? from(readFiles(mailAttachments)) : of([])

    return attachments
      .pipe(
        switchMap(attachments => {
          const body: any = {}
          if (attachments.length)
            body.mailAttachments = attachments

          return this.http.patch<void>(`${this.baseUrl}/Cart/OrderStatus`, body, { params })
        }))
  }

  copyOrder(): Observable<{ orderId: string }> {
    return this.http.post<{ orderId: string }>(`${this.baseUrl}/Cart/CopyOrder`, null)
  }

  updatePayment(paymentMethod: string, paymentStatus: PaymentStatus, paidAmount: number): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/Cart/UpdatePayment`, null, {
      params: {
        paymentMethod,
        paymentStatus: paymentStatus.toString(),
        paidAmount: paidAmount.toString()
      }
    })
  }

  addOrderNote(reservationSetId: number, message: string, completed: boolean, allowEditByEveryone: boolean) {
    return this.http.post<void>(`${this.baseUrl}/Notes/ReservationSet(${reservationSetId})`, { message, completed, allowEditByEveryone })
  }

  updateOrderNote(id: number, message: string, completed: boolean, allowEditByEveryone: boolean) {
    return this.http.put<void>(`${this.baseUrl}/Notes(${id})`, { message, completed, allowEditByEveryone })
  }

  deleteOrderNote(id: number) {
    return this.http.delete<void>(`${this.baseUrl}/Notes(${id})`)
  }

  //#endregion

  //#region Terms and Conditions

  deleteTermsAndConditions(entityId: number) {
    return this.http.delete<void>(`${this.baseUrl}/TermsAndConditions(${entityId})`)
  }

  //#endregion

  //#region Calendar Shares

  getCalendarShares(type: CalendarShareType): Observable<CalendarShareModel[]> {
    return this.http.get<CalendarShareModel[]>(`${this.baseUrl}/CalendarShares/${type}`)
  }

  addCalendarShare(type: CalendarShareType, title: string, internalTitle: string, notes: string, showAllBookingDetails: boolean,
    pastEvents: CalendarSyncDuration, futureEvents: CalendarSyncDuration, allItems: boolean,
    items: FieldLookupValue[], showPurpose?: boolean, showBookingLink?: boolean) {
    return this.http.post<CalendarShareModel>(`${this.baseUrl}/CalendarShares`, {
      type, title, internalTitle, notes, key: '', showAllBookingDetails, pastEvents, futureEvents, allItems, items, showPurpose, showBookingLink
    })
  }

  updateCalendarShare(id: number, type: CalendarShareType, title: string, internalTitle: string, notes: string, showAllBookingDetails: boolean,
    pastEvents: CalendarSyncDuration, futureEvents: CalendarSyncDuration, allItems: boolean,
    items: FieldLookupValue[], showPurpose?: boolean, showBookingLink?: boolean) {
    return this.http.put<CalendarShareModel>(`${this.baseUrl}/CalendarShares(${id})`, {
      type, title, internalTitle, notes, key: '', showAllBookingDetails, pastEvents, futureEvents, allItems, items, showPurpose, showBookingLink
    })
  }

  deleteCalendarShare(id: number) {
    return this.http.delete(`${this.baseUrl}/CalendarShares(${id})`)
  }

  //#endregion

  // #region Texts and Labels

  getTextsAndLabels() {
    return this.http.get<TextsAndLabels[]>(`${this.baseUrl}/TextsAndLabels`)
  }
}
